var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef } from 'react';
import BottomPanel from './BottomPanel';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { leftSideTools } from '../interface/gameController';
import '../styles/toolBottomPanel.scss';
// Tools that should show toolBottomPanel when active (even without selectedTool)
export var toolsWithBottomPanel = [
    leftSideTools.pencil,
    leftSideTools.highlighter,
    leftSideTools.pen,
    leftSideTools.eraser,
];
var ToolBottomPanel = function () {
    var _a = useContext(DataContext), selectedTool = _a.selectedTool, tool = _a.tool, annotations = _a.annotations, thickness = _a.thickness;
    var containerRef = useRef(null);
    var _b = globalStore.useStoreState(function (state) { return state.jsonDataStore; }), btnConfig = _b.btnConfig, bottomConfigStore = _b.bottomConfig;
    var bottomConfig = useMemo(function () {
        // First, check if there's a selectedTool
        if (selectedTool.length > 0) {
            // Get configs for all selected tools
            var configs_1 = selectedTool
                .map(function (tool) {
                var toolKey = tool.split('_')[0];
                if (tool.includes('annotation')) {
                    var num = Number(tool.split('_')[1]);
                    return bottomConfigStore[annotations[num].type];
                }
                return bottomConfigStore[toolKey];
            })
                .filter(function (config) { return config !== null && config !== undefined; });
            if (configs_1.length > 0) {
                // Find common config (intersection of all configs)
                if (configs_1.length === 1) {
                    return configs_1[0];
                }
                // Find common elements across all configs
                var commonConfig = configs_1[0].filter(function (item) {
                    return configs_1.every(function (config) {
                        // Check if item exists in all other configs
                        return config.some(function (configItem) {
                            // Compare by value if both are strings, or by reference if objects
                            if (typeof item === 'string' && typeof configItem === 'string') {
                                return item === configItem;
                            }
                            // For objects, compare by reference or by a unique identifier if available
                            return item === configItem;
                        });
                    });
                });
                return commonConfig.length > 0 ? commonConfig : null;
            }
        }
        // If no selectedTool config found, check if the active tool should show bottomPanel
        if (toolsWithBottomPanel.includes(tool)) {
            var toolKey = tool;
            return bottomConfigStore[toolKey] || null;
        }
        return null;
    }, [selectedTool, tool, bottomConfigStore]);
    var hasContent = Boolean(bottomConfig && bottomConfig.length > 0);
    // Compute which highlighterCollapsible index matches the current thickness
    // thickness = index * 4 + base (pen: 3, highlighter: 11, eraser: 23)
    var widthBase = tool === leftSideTools.eraser ? 23 : tool === leftSideTools.highlighter ? 11 : 3;
    var activeWidthIndex = Math.round((thickness - widthBase) / 4);
    var setupToolButton = function (toolBtn) {
        if (!toolBtn)
            return [];
        return toolBtn
            .map(function (_tool) {
            if (typeof _tool === 'string') {
                var btnConfigItem = btnConfig[_tool];
                if (btnConfigItem) {
                    var item = __assign(__assign({}, btnConfigItem), { id: _tool });
                    // Mark width buttons so they can show active state
                    if (_tool.startsWith('highlighterCollapsible_')) {
                        item.activeState = true;
                    }
                    return item;
                }
                // If not found in btnConfig, skip it (invalid reference)
                console.warn("ToolBottomPanel: btnConfig key \"".concat(_tool, "\" not found"));
                return null;
            }
            return _tool;
        })
            .filter(function (item) { return item !== null; });
    };
    var toolsBtn = setupToolButton(bottomConfig);
    // Build collapsibleState for width buttons so the active one is highlighted
    var widthCollapsibleState = useMemo(function () {
        return [0, 1, 2, 3].map(function (i) { return ({
            id: "highlighterCollapsible_".concat(i),
            activeElements: null,
            activeCollapsible: false,
            isActive: i === activeWidthIndex,
        }); });
    }, [activeWidthIndex]);
    function getStyle() {
        var _a;
        var footerHeight = ((_a = document.querySelector('.footer')) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) || 0;
        return {
            bottom: "".concat(footerHeight + 25, "px"),
        };
    }
    // Cleanup effect to ensure DOM elements are properly removed
    useEffect(function () {
        return function () {
            // Force cleanup of any remaining DOM references
            if (containerRef.current) {
                // Remove all child nodes
                while (containerRef.current.firstChild) {
                    containerRef.current.removeChild(containerRef.current.firstChild);
                }
            }
        };
    }, []);
    // Annotations (lines, shapes) bottom panel should not be keyboard focusable
    var isAnnotationPanel = selectedTool.some(function (s) { return s.startsWith('annotations_'); });
    return (_jsx("div", { ref: containerRef, className: "toolBottomPanelContainer", style: getStyle(), onMouseDown: function (e) {
            var _a;
            // Prevent bottom panel clicks from stealing focus from math-fields,
            // so MathLive selection highlight stays visible while using toolbar buttons.
            var active = document.activeElement;
            if ((active === null || active === void 0 ? void 0 : active.tagName) === 'MATH-FIELD' || ((_a = active === null || active === void 0 ? void 0 : active.closest) === null || _a === void 0 ? void 0 : _a.call(active, 'math-field'))) {
                e.preventDefault();
            }
        }, children: _jsx(BottomPanel, { id: "toolBottomPanel", toolsBtn: toolsBtn, className: "toolBottomPanel", collapsibleState: widthCollapsibleState, disableTabFocus: isAnnotationPanel }, "toolBottomPanel") }));
};
export default ToolBottomPanel;
